import { ProductService } from "../ProductService";
import { useForm } from "react-hook-form";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { INVALID_PACKAGE_CODE } from "../../utils/constants";
import { type HousingProduct, isKITProduct, type PackagingInformationEntry } from "../../models/ProductData";
import { type NetPriceData } from "../../models/PriceData";
import { useSelector } from "react-redux";
import { selectProperAmountInCart } from "../redux/cartAmounts/cartAmountSelectors";
import { getNetPrice, getSurcharge, getTariff } from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { toMapFn } from "../../utils/reducers";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { getStore } from "~/redux/getStore";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

export interface AddToCartHookProps {
  productCode: string;

  initialData?: Partial<AddToCartFormData>;
  initialPackCodeProvider?: PackCodeProvider | false;
  onInitialPackCodeSelection?: (packCode: string) => void;

  editMode?: boolean;
  calculatePrice?: boolean;

  fetchAvailability?: boolean;
}

export interface AddToCartFormData {
  quantity: number;
  priceReference: string;
  selectedPackCode: string;
  selectedPack: PackagingInformationEntry | null;

  optionalParts: Dict<OptionalPartFormData>;

  netPrice: NetPriceData | null;

  surcharge: number;
  tariffSurcharge?: boolean;
  priceUnit: number;
  // Total qty including items in cart
  totalQuantity: number;

  comment?: string;

  salePriceEnabled: boolean;
}

export interface OptionalPartFormData {
  productId: string;
  selected: boolean;
  quantity: number;
  selectedPackCode: string;
  isQtyUserModified: boolean;

  netPrice: NetPriceData | null;
  surcharge: number;
  tariffSurcharge: boolean;
  priceUnit: number;
}

export type PackCodeProvider = (product: ProductDescriptor, quantity: number) => string;

export const useAddToCartForm = ({
  productCode,
  initialPackCodeProvider,
  onInitialPackCodeSelection,
  initialData: rawInitialData,
  editMode,
  calculatePrice = true,
  fetchAvailability = true,
}: AddToCartHookProps) => {
  const [initialData] = useState<AddToCartFormData>(() => createInitialData(rawInitialData, productCode));
  const formProps = useForm<AddToCartFormData>({ defaultValues: initialData });

  const priceReference = formProps.watch("priceReference");
  const productDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference,
    options: { fetch: true, fetchAvailability },
  });

  // Register the fields so we can modify them
  useLayoutEffect(() => {
    formProps.register("quantity");
    formProps.register("priceReference");
    formProps.register("selectedPackCode");
    formProps.register("selectedPack");
    formProps.register("netPrice");
    formProps.register("surcharge");
    formProps.register("tariffSurcharge");
    formProps.register("priceUnit");
    formProps.register("totalQuantity");
    formProps.register("optionalParts");
    formProps.register("comment");
    formProps.register("salePriceEnabled");
  }, []);

  // Force rerender after every change - BAD for performance, but out of ideas right now
  // noinspection JSUnusedLocalSymbols

  const data = formProps.watch();

  const selectedPackCode = formProps.watch("selectedPackCode");

  // Select the initial package if there is none
  useEffect(() => {
    if (!editMode && formProps.getValues("selectedPackCode") === INVALID_PACKAGE_CODE && productDescriptor.isLoaded) {
      const packageProvider = initialPackCodeProvider ?? makeSalesAwarePackCodeProvider(null);
      if (packageProvider) {
        const selectedPackCode =
          packageProvider(productDescriptor, formProps.getValues("quantity")) ?? INVALID_PACKAGE_CODE;
        formProps.setValue("selectedPackCode", selectedPackCode);
        if (onInitialPackCodeSelection) {
          onInitialPackCodeSelection(selectedPackCode);
        }
      }
    }
  }, [editMode, productDescriptor.isLoaded]);

  const quantity = formProps.watch("quantity");

  // Modify optional part quantity
  useEffect(() => {
    if (!isKITProduct(productDescriptor.base)) {
      return;
    }
    const priceReference = formProps.getValues("priceReference");
    const optionalPartsList = Object.values(formProps.getValues("optionalParts")) as OptionalPartFormData[];
    const hasAnyNonUserQty = optionalPartsList.some((item) => !item.isQtyUserModified);
    if (hasAnyNonUserQty) {
      // Normally let's not grab state directly this way
      // but here we need to recalculate prices due to parent line item qty changing, nothing else
      const state = getStore().getState();
      const housingProductsById = toMapFn(productDescriptor.base.optionalParts, (item) => item.id);
      const optionalParts = toMapFn(
        optionalPartsList.map((item) => {
          if (item.isQtyUserModified) {
            return item;
          } else {
            const priceProps = {
              productCode: item.productId,
              priceReference,
              packCode: item.selectedPackCode,
            };

            const newQty = quantity * (housingProductsById[item.productId]?.quantity ?? 1);
            const cartQty = selectProperAmountInCart(priceProps)(state);
            const optionalDescriptor = ProductService.getProductDescriptor(item.productId, priceReference).current;
            const selectPriceProps = {
              ...priceProps,
              totalQuantity: newQty + cartQty,
              product: optionalDescriptor,
            };
            const newPrice = getNetPrice(selectPriceProps);
            const newSurcharge = getSurcharge(selectPriceProps);
            const tariffSurcharge = getTariff(selectPriceProps.product);
            const newItem = {
              ...item,
              quantity: quantity * (housingProductsById[item.productId]?.quantity ?? 1),
              netPrice: newPrice,
              surcharge: newSurcharge,
              tariffSurcharge: tariffSurcharge,
              priceUnit: newPrice?.unit ?? item.priceUnit,
            };

            return newItem;
          }
        }),
        (item) => item.productId,
      );
      formProps.setValue("optionalParts", optionalParts);
    }
  }, [quantity]);

  const cartQty = useSelector(
    selectProperAmountInCart({ productCode, packCode: formProps.watch("selectedPackCode"), priceReference }),
  );

  const totalQty = quantity - (editMode ? rawInitialData?.quantity ?? 0 : 0) + cartQty;
  useLayoutEffect(() => {
    if (totalQty !== formProps.getValues("totalQuantity")) {
      formProps.setValue("totalQuantity", totalQty);
    }
  }, [totalQty]);

  // Update price information
  useLayoutEffect(() => {
    if (calculatePrice && productDescriptor.prices.length) {
      const selectPriceProps = { totalQuantity: totalQty, product: productDescriptor, packCode: selectedPackCode };
      const price = getNetPrice(selectPriceProps);
      const surcharge = getSurcharge(selectPriceProps);
      const tariffSurcharge = getTariff(selectPriceProps.product);

      const priceUnit = price?.unit ?? 1;
      if (priceUnit !== formProps.getValues("priceUnit")) {
        formProps.setValue("priceUnit", priceUnit);
      }

      const currPrice = formProps.getValues("netPrice");
      if (price !== currPrice && price?.value !== currPrice?.value) {
        formProps.setValue("netPrice", price);
      }

      const currSurcharge = formProps.getValues("surcharge");
      if (surcharge !== currSurcharge) {
        formProps.setValue("surcharge", surcharge);
      }

      const tarrSurcharge = formProps.getValues("tariffSurcharge");
      if (tariffSurcharge !== tarrSurcharge) {
        formProps.setValue("tariffSurcharge", tariffSurcharge);
      }
    }
  }, [calculatePrice, quantity, productDescriptor.isLoaded, totalQty, selectedPackCode, priceReference]);

  const updatedFormProps = useMemo(() => {
    return {
      ...formProps,
      // eslint-disable-next-line
      setValue: (name: any, value: any, options?: any) => {
        formProps.setValue(name, value, options);
        if (!editMode && name === "selectedPack" && !formProps.formState.touchedFields.quantity) {
          const selectedPack = value as PackagingInformationEntry | null;
          if (selectedPack?.packSize) {
            const packSize = selectedPack.packSize;
            formProps.setValue("quantity", packSize, { shouldDirty: true });
          }
        }
      },
    };
  }, [formProps, editMode]);

  // Synchronize values
  useEffect(() => {
    const prevPack = updatedFormProps.getValues("selectedPack");
    if (prevPack?.packCode === selectedPackCode) {
      return;
    }

    const selectedPack =
      productDescriptor.availability?.packagingInformation.find(
        (packaging) => packaging.packCode === selectedPackCode,
      ) ?? null;
    updatedFormProps.setValue("selectedPack", selectedPack);
  }, [selectedPackCode, productDescriptor.availability]);

  return [updatedFormProps, productDescriptor] as const;
};

function createInitialData(initialData: Partial<AddToCartFormData> = {}, productCode: string): AddToCartFormData {
  const product = ProductService.getBaseProductData(productCode);
  const optionalParts = (isKITProduct(product) && product?.optionalParts) || [];
  const qty = initialData.quantity ?? 1;
  const finalInitialData = {
    quantity: qty,
    priceReference: getInitialPriceReference(),
    selectedPackCode: INVALID_PACKAGE_CODE,
    optionalParts: makeInitialOptionalPartsFormData(optionalParts, qty),
    selectedPack: null, // Will be synced by useEffect
    netPrice: null, // Will be synced by useEffect
    surcharge: 0, // Will be synced by useEffect
    tariffSurcharge: false, // Will be synced by useEffect
    priceUnit: 1, // Will be synced by useEffect
    totalQuantity: 1, // Will be synced by useEffect
    comment: "",
    salePriceEnabled: true,
    ...initialData,
  };
  return finalInitialData;
}

export const makeInitialOptionalPartsFormData = (parts: HousingProduct[], kitQty = 1): Dict<OptionalPartFormData> => {
  return toMapFn(
    parts.map((item) => ({
      productId: item.id,
      selected: true,
      quantity: item.quantity * kitQty,
      selectedPackCode: INVALID_PACKAGE_CODE,
      isQtyUserModified: false,

      netPrice: null,
      priceUnit: 1,
    })),
    (item) => item.productId,
  );
};
